import Canvas from "./Canvas";
import requestHelper from "./../api/requestHelper";
import AppDebug from "../AppDebug";
import apiRequestHelper from "../api/requestHelper";
import CreatableProductConfigurationView from "./../types/CreatableProductConfigurationView";

class ProductConfiguration {

    /**
     * Id of the selected product to be refined.
     * @type number
     */
    _productId;

    /**
     * Uuid of die ProductConfiguration that will be load from API.
     * @type string
     */
    _productConfigurationId;

    /**
     * Id the parent product configuration to build a history.
     * @type ?number
     */
    _parentId;

    /**
     * The types of views that can be selected when creating new ProductConfigurationViews.
     * @type Array<CreatableProductConfigurationView>
     */
    _createableProductViews;

    /**
     * Views of the ProductConfiguration.
     * @type Array<ProductConfigurationView>
     */
    _productConfigurationViews;

    _hasBlueprints;

    // Listeners that are called when an event occurs.
    _listeners;

    /**
     * Create a ProductConfiguration.
     * @param productId
     * @param productConfigurationId
     */
    constructor(productId, productConfigurationId = null) {
        this._productId = productId;
        this._parentId = null;
        this._listeners = {};
        this._productConfigurationViews = [];
        this._createableProductViews = [];
        this._hasBlueprints = false;

        if (productConfigurationId) {
            this._productConfigurationId = productConfigurationId;
        } else {
            this._productConfigurationId = null;
        }

        this.registerEventListener('loadedCreateableProductViews', this.loadedCreateableProductViews);

        if (productId) {
            this.loadCreateableProductViews();
            // Checks if blueprints have been created for the product.
            // If so, they are added as ProductConfigurationViews and it is no longer possible to create more.

        }
    }

    async loadedCreateableProductViews({productConfiguration, createableProductViews}) {
        console.log('init defaults and existing views', { pcid: productConfiguration.getProductConfigurationId() });
        if (productConfiguration.getProductConfigurationId()) {
            await productConfiguration.loadExistingProductConfiguration(productConfiguration.getProductConfigurationId());
        }
        await productConfiguration.checkIfProductHasBlueprintViews();
        productConfiguration.createDefaultProductConfigurationViewsWhenLoadProduct();
    }

    /**
     * Loads the Views associated with the Product that can be refined.
     * @return {Promise<void>}
     */
    async loadCreateableProductViews() {

        let productViews = await requestHelper.requestApiRoute('get', `/product/${this.getProductId()}/view`);

        productViews = productViews.map(productView => {
            productView.backgroundUrl = requestHelper.createUrl(`/viewimage/${productView.backgroundId}`);
            productView.previewUrl = requestHelper.createUrl(`/viewimage/${productView.previewId}`);
            return productView;
        });

        this.setCreateableProductViews(productViews);
        this.fireEvent('loadedCreateableProductViews', {productConfiguration: this, createableProductViews: productViews});

    }

    /**
     * Checks if blueprints have been created for the product. If so, they are added as ProductConfigurationViews and it is no longer possible to create more.
     * @returns {Promise<void>}
     */
    async checkIfProductHasBlueprintViews() {

        let productViews = await requestHelper.requestApiRoute('get', `/blueprint/${this.getProductId()}`);

        console.log({productViews});

        if (productViews !== null) {

            Object.keys(productViews).forEach(key => {

                const productConfigurationView = productViews[key];

                if (this.getProductConfigurationViews().some(pcv => pcv.productView.id === productConfigurationView.viewId))
                    return;

                this._hasBlueprints = true;

                /** @type ProductConfigurationView */
                const createdView = this.loadProductConfigurationView(productConfigurationView);

                if (!createdView)
                    window.location = window.location;

                if (window['w2p_placeholder']) {
                    productConfigurationView.canvasObjects.forEach(canvasObject => {
                        if (canvasObject.serializedObject.text) {
                            if (canvasObject.serializedObject.text.value) {
                                canvasObject.serializedObject.text.value = canvasObject.serializedObject.text.value.interpolate(window['w2p_placeholder']);
                            }
                        }
                    });
                }

                if (window['w2p_second_language'] === true) {
                    console.log(productConfigurationView);
                    productConfigurationView.canvasObjects.forEach(canvasObject => {
                        if (canvasObject.serializedObject.text && canvasObject.serializedObject.textSecondLanguage) {
                            if (canvasObject.serializedObject.text.value && canvasObject.serializedObject.textSecondLanguage.value) {
                                canvasObject.serializedObject.text.value = canvasObject.serializedObject.textSecondLanguage.value;
                            }
                        }
                        if (canvasObject.serializedObject.label && canvasObject.serializedObject.labelSecondLanguage) {
                            if (canvasObject.serializedObject.label.value && canvasObject.serializedObject.labelSecondLanguage.value) {
                                canvasObject.serializedObject.label.value = canvasObject.serializedObject.labelSecondLanguage.value;
                            }
                        }
                    });
                }

                createdView.canvas.createCanvasObjectsFromAPIResponse(
                    productConfigurationView.canvasObjects
                );

            });

        }

    }

    /**
     * Create and add a new ProductConfigurationView based on a ProductView.
     * @param {number} productViewId Which of the createable views used as blueprint.
     * @returns {ProductConfigurationView} Instance of the new created ProductConfigurationView.
     */
    createNewProductConfigurationView(productViewId) {

        if (! productViewId) {
            alert('ProductConfigurationView auswählen.');
            return;
        }

        /** @type CreatableProductConfigurationView */
        const productConfigurationView = this.findCreateableProductViewById( productViewId );

        if (productConfigurationView) {

            const newProductConfigurationView = Object.assign({}, productConfigurationView);

            newProductConfigurationView.canvas = new Canvas(
                requestHelper.createUrl(`/viewimage/${newProductConfigurationView.backgroundId}`),
                newProductConfigurationView.id,
                newProductConfigurationView.dpi,
                newProductConfigurationView.factor
            );

            newProductConfigurationView.canvas.setCreateableObjectTypes(productConfigurationView.allowedObjectTypes);
            newProductConfigurationView.canvas.setArticleNumber(productConfigurationView.articleNumber);

            newProductConfigurationView.active = false;
            newProductConfigurationView.preview = requestHelper.createUrl(`/viewimage/${newProductConfigurationView.previewId}`);
            newProductConfigurationView.productView = {
                id: newProductConfigurationView.id
            };
            newProductConfigurationView.id = 'pcv_' + Math.random().toString(36).substr(2, 9) + new Date().getTime();

            this.getProductConfigurationViews().push(
                newProductConfigurationView
            );

            this.setActiveProductConfigurationView(newProductConfigurationView.id);

            window.EventHandler.$emit('clearobjectview');

            return newProductConfigurationView;

        }

    }

    /**
     * Create and add new ProductConfigurationViews with defined article numbers if they defined as default and are not load by existing ProductConfiguration for the product.
     * @returns {ProductConfigurationView[]} Instances of the new created ProductConfigurationViews.
     */
    createDefaultProductConfigurationViewsWhenLoadProduct() {

        const
            // Array of article numbers
            defaultViews = window['w2p_default_views_by_article_number'],
            createableViews = this.getCreateableProductViews();

        console.log({createableViews});

        createableViews.forEach(createableView => {

            if (defaultViews.find(articleNumber => createableView.articleNumber === articleNumber)) {

                if (! this.getProductConfigurationViews().find(productConfigurationView => productConfigurationView.getArticleNumber() === createableView.articleNumber)) {

                    this.createNewProductConfigurationView(createableView.id);

                }

            }

        });
    }

    /**
     * Add a new ProductConfigurationView based on a existing ProductConfigurationView provided by the API.
     * @param {ResponseProductConfigurationView} productConfigurationView
     * @return {ProductConfigurationView}
     */
    loadProductConfigurationView(productConfigurationView) {

        console.log('loadProductConfigurationView', {productConfigurationView});

        const canvas = new Canvas(
            requestHelper.addDomainToRoute(productConfigurationView.backgroundImage.url),
            productConfigurationView.viewId,
            productConfigurationView.printDpi,
            productConfigurationView.pixelInMmFactor
        );

        canvas.setArticleNumber(productConfigurationView.articleNumber);
        canvas.setCreateableObjectTypes(productConfigurationView.allowedObjectTypes);

        /**
         * @type {ProductConfigurationView}
         */
        const newProductConfigurationView = {
            id: 'pcv_' + Math.random().toString(36).substr(2, 9) + new Date().getTime(),
            productId: productConfigurationView.productId,
            productView: {
                id: productConfigurationView.viewId
            },
            active: false,
            backgroundId: productConfigurationView.backgroundImage.id,
            backgroundUrl: requestHelper.addDomainToRoute(productConfigurationView.backgroundImage.url),
            previewId: productConfigurationView.previewImage.id,
            previewUrl: requestHelper.addDomainToRoute(productConfigurationView.previewImage.url),
            boundingBox: productConfigurationView.boundingBox,
            canvas,
            canvasHeight: productConfigurationView.canvasHeight,
            canvasWidth: productConfigurationView.canvasWidth,
            title: productConfigurationView.title,
            description: productConfigurationView.description,
            dpi: productConfigurationView.printDpi,
            factor: productConfigurationView.pixelInMmFactor,
            printType: productConfigurationView.printType,
            printArea: productConfigurationView.printArea
        };

        newProductConfigurationView.boundingBox.x.current = productConfigurationView.boundingBoxX;
        newProductConfigurationView.boundingBox.y.current = productConfigurationView.boundingBoxY;
        newProductConfigurationView.canvas.setCanAddElements(productConfigurationView.canAddElements);
        newProductConfigurationView.canvas.setCanSortElements(productConfigurationView.canSortElements);

        this.getProductConfigurationViews().push(
            newProductConfigurationView
        );

        this.setActiveProductConfigurationView(newProductConfigurationView.id);

        window.EventHandler.$emit('clearobjectview');

        return newProductConfigurationView;

    }

    /**
     * Set the ProductConfigurationView with defined id to active. Used to determine which is the currently visible ProductConfigurationView.
     * @param {number} productViewConfigurationId
     */
    setActiveProductConfigurationView(productViewConfigurationId) {

        const lastActive = this.getProductConfigurationViews().find( view => view.active === true );

        if (lastActive) lastActive.active = false;

        const nowActive = this.getProductConfigurationViews().find( view => view.id === productViewConfigurationId );

        if (nowActive) nowActive.active = true;

        AppDebug.views.event.chanceActiveTab({
            lastActive,
            newActive: nowActive
        });

    }

    /**
     * Loads information about the ProductConfiguration with id defined in the query string.
     * @param productConfigurationId
     * @returns {Promise<void>}
     */
    async loadExistingProductConfiguration(productConfigurationId) {

        /** @type ResponseProductConfiguration */
        const response = await requestHelper.requestApiRoute(
            'get',`/configuration/${productConfigurationId}/view`
        );

        console.log('load existing configuration', response);

        if (! response) {
            alert('Gibt keine ProductConfiguration mit der ID.');
            window.location = new URL(window.location.href).origin + new URL(window.location.href).pathname;
        }

        this.setParentId(
            response.productConfiguration.parent === null ? response.productConfiguration.id : response.productConfiguration.parent
        );

        this.setProductId( response.productConfiguration.product.id );

        // Create a Canvas type object for each of the ProductConfigurationViews associated with this ProductConfiguration in the database.
        response.productConfigurationViews.forEach(
            /** @param {ResponseProductConfigurationView} productConfigurationView */
            productConfigurationView => {

                /** @type ProductConfigurationView */
                const createdView = this.loadProductConfigurationView( productConfigurationView );

                if (!createdView)
                    window.location = window.location;

                createdView.canvas.createCanvasObjectsFromAPIResponse(
                    productConfigurationView.canvasObjects
                );

            }

        );

        console.log(response);

    }

    /**
     * Create a new ProductConfiguration with Views and Objects in the database.
     * @returns {Promise<Object>}
     */
    async save() {

        const createdProductConfiguration = await apiRequestHelper.requestApiRoute('post', `/configuration`, {
            productId: this.getProductId(),
            parentProductConfigurationId: this.getParentId(),
            isBlueprint: window['w2p_admin'] === true
        });

        if (createdProductConfiguration)
            if (createdProductConfiguration.hasOwnProperty('id')) {
                const productConfigurationViews = this.getProductConfigurationViews();
                for (let i = 0; i < productConfigurationViews.length; i++) {
                    const productConfigurationView = productConfigurationViews[i];
                    await productConfigurationView.canvas.save(createdProductConfiguration.id);
                }
                window.EventHandler.$emit('savefinished');
                //alert(new URL(window.location.href).origin + new URL(window.location.href).pathname + '?prodconf=' + createdProductConfiguration.id);
                return createdProductConfiguration;
            }

    }

    fireEvent(eventName, data) {

        const
            listeners = this._listeners;

        if (listeners.hasOwnProperty(eventName)) {

            const listenersForCurrentEvent = listeners[eventName];

            if (Array.isArray(listenersForCurrentEvent)) {

                listenersForCurrentEvent.forEach(listener => listener(data));

            }

        }

    }

    registerEventListener(eventName, callback) {

        const
            listeners = this._listeners;

        if (listeners[eventName]) {
            listeners[eventName].push(callback);
        } else {
            listeners[eventName] = [callback];
        }

    }

    /**
     *
     * @param {number} productViewId
     */
    findCreateableProductViewById(productViewId) {
        return this.getCreateableProductViews().find(view => view.id === productViewId);
    }

    hasBlueprints() {
        return this._hasBlueprints;
    }

    getProductId() {
        return this._productId;
    }

    setProductId(value) {
        this._productId = value;
    }

    getProductConfigurationId() {
        return this._productConfigurationId;
    }

    getCreateableProductViews() {
        return this._createableProductViews;
    }

    getParentId() {
        return this._parentId;
    }

    setParentId(value) {
        this._parentId = value;
    }

    setCreateableProductViews(value) {
        this._createableProductViews = value;
    }

    getProductConfigurationViews() {
        return this._productConfigurationViews;
    }

}

export default ProductConfiguration;
